import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import ScrollSnapWheel from './ScrollSnapWheel'

interface BatteryWheelPickerProps {
  value: number
  onChange: (v: number) => void
  min?: number
  max?: number
  step?: number
  /** Inline spec row: wheel only, no large current-value badge. */
  compact?: boolean
}

function batteryColor(v: number): string {
  if (v >= 85) return '#10b981'
  if (v >= 70) return '#3b82f6'
  if (v >= 50) return '#f59e0b'
  return '#ef4444'
}

export default function BatteryWheelPicker({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  compact = false,
}: BatteryWheelPickerProps) {
  const { t } = useTranslation()
  const batteryLabel = t('listing.form.batteryLabel')
  const items = useMemo(() => {
    const nums: number[] = []
    for (let n = max; n >= min; n -= step) nums.push(n)
    return nums
  }, [min, max, step])

  const color = batteryColor(value)

  return (
    <div className={`battery-scroll-field${compact ? ' battery-scroll-field--compact' : ''}`}>
      {!compact ? (
        <div className="battery-scroll-current" style={{ color, borderColor: `${color}44` }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <rect x="2" y="7" width="18" height="10" rx="2" />
            <rect x="20" y="10" width="2" height="4" rx="1" fill="currentColor" />
            <rect
              x="4"
              y="9"
              width={Math.max(1, (value / 100) * 14)}
              height="6"
              rx="1"
              fill="currentColor"
              stroke="none"
            />
          </svg>
          <span>{value}%</span>
        </div>
      ) : null}
      <ScrollSnapWheel
        className="battery-scroll-wheel"
        value={value}
        onChange={onChange}
        items={items}
        ariaLabel={batteryLabel}
        formatItem={(n) => `${n}%`}
        getActiveStyle={(n) => ({ color: batteryColor(n) })}
        compact={compact}
      />
      <input
        type="number"
        className="battery-wheel__a11y"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => {
          const parsed = parseInt(e.target.value, 10)
          if (Number.isFinite(parsed)) onChange(Math.max(min, Math.min(max, parsed)))
        }}
        aria-label={batteryLabel}
      />
    </div>
  )
}
